import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { api, type ApiResponse } from "@/api/client";
import { supplierComponentCreateSchema } from "@ckgo2/shared/schemas";
import {
  SERVICE_KINDS,
  COST_SCOPES,
  BOOKING_MODES,
  SERVICE_KIND_LABELS,
  COST_SCOPE_LABELS,
  BOOKING_MODE_LABELS,
  type Destination,
  type ServiceKind,
  type CostScope,
  type BookingMode,
} from "@ckgo2/shared/types";
import RoomTypeVariantsEditor, {
  type RoomTypeVariant,
} from "@/components/common/RoomTypeVariantsEditor";

interface FormData {
  name: string;
  kind: ServiceKind;
  supplierId: string | null;
  destinationId: string | null;
  sellPrice: number;
  cost: number;
  currency: string;
  costScope: CostScope;
  bookingMode: BookingMode;
  notes: string;
  isActive: boolean;
  roomTypes: RoomTypeVariant[];
}

interface SupplierComponent extends FormData {
  id: string;
}

interface SupplierLite {
  id: string;
  name: string;
  bookingMode?: BookingMode;
}

export default function SupplierComponentEditPage() {
  const { data: destData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });
  const { data: supData } = useQuery({
    queryKey: ["suppliers", "lookup"],
    queryFn: () => api.get<{ data: SupplierLite[] }>("/api/admin/suppliers?limit=100"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FormData,
    SupplierComponent
  >({
    resourceKey: "supplierComponent",
    listKey: "supplierComponents",
    apiPath: "/api/admin/supplier-components",
    schema: supplierComponentCreateSchema,
    defaultValues: {
      name: "",
      kind: "hotel",
      supplierId: null,
      destinationId: null,
      sellPrice: 0,
      cost: 0,
      currency: "CZK",
      costScope: "per_pax",
      bookingMode: "confirm_required",
      notes: "",
      isActive: true,
      roomTypes: [],
    },
    navigateTo: "/supplier-components",
    transformOnLoad: (s) => ({
      name: s.name || "",
      kind: s.kind || "hotel",
      supplierId: s.supplierId ?? null,
      destinationId: s.destinationId ?? null,
      sellPrice: s.sellPrice ?? 0,
      cost: s.cost ?? 0,
      currency: s.currency || "CZK",
      costScope: s.costScope || "per_pax",
      bookingMode: s.bookingMode || "confirm_required",
      notes: s.notes || "",
      isActive: s.isActive ?? true,
      roomTypes: (s.roomTypes ?? []).map((v) => ({
        id: v.id,
        roomType: v.roomType,
        cost: v.cost ?? 0,
        currency: v.currency ?? null,
        sortOrder: v.sortOrder ?? 0,
      })),
    }),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = form;

  const margin = (watch("sellPrice") || 0) - (watch("cost") || 0);

  // Prefill bookingMode from the chosen supplier on a NEW component ("our own boat" set on the
  // supplier flows to its catalogue items). Editing an existing component preserves its stored value.
  const watchedSupplierId = watch("supplierId");
  useEffect(() => {
    if (!isNew || !watchedSupplierId) return;
    const sup = (supData?.data ?? []).find((s) => s.id === watchedSupplierId);
    if (sup?.bookingMode) setValue("bookingMode", sup.bookingMode);
  }, [isNew, watchedSupplierId, supData, setValue]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">Component not found</p>
        <Link to="/supplier-components" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/supplier-components"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New component" : "Edit component"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Add a priced service to the catalog" : `Editing: ${itemData?.name}`}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="name" className="label">
                Name *
              </label>
              <input
                {...register("name")}
                type="text"
                id="name"
                className="input"
                placeholder="e.g. Halong Bay overnight cruise — deluxe cabin"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="kind" className="label">
                  Kind
                </label>
                <select {...register("kind")} id="kind" className="input">
                  {SERVICE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {SERVICE_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="supplierId" className="label">
                  Supplier
                </label>
                <select {...register("supplierId")} id="supplierId" className="input">
                  <option value="">No supplier</option>
                  {(supData?.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="destinationId" className="label">
                Destination
              </label>
              <select {...register("destinationId")} id="destinationId" className="input">
                <option value="">Global (any destination)</option>
                {(destData?.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="notes" className="label">
                Notes
              </label>
              <textarea
                {...register("notes")}
                id="notes"
                rows={3}
                className="input"
                placeholder="What's included, booking conditions…"
              />
            </div>
          </div>

          <div className="card">
            <RoomTypeVariantsEditor
              value={watch("roomTypes") || []}
              onChange={(v) => setValue("roomTypes", v, { shouldDirty: true })}
              currency={watch("currency")}
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cost" className="label">
                  Cost
                </label>
                <input
                  {...register("cost", { valueAsNumber: true })}
                  type="number"
                  id="cost"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="sellPrice" className="label">
                  Sell price
                </label>
                <input
                  {...register("sellPrice", { valueAsNumber: true })}
                  type="number"
                  id="sellPrice"
                  className="input"
                />
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800/50">
              <span className="text-gray-500">Margin: </span>
              <span
                className={
                  margin >= 0
                    ? "font-medium text-emerald-600 dark:text-emerald-400"
                    : "font-medium text-red-600 dark:text-red-400"
                }
              >
                {margin}
              </span>
            </div>

            <div>
              <label htmlFor="currency" className="label">
                Currency
              </label>
              <input
                {...register("currency")}
                type="text"
                id="currency"
                maxLength={3}
                className="input uppercase"
                placeholder="CZK"
              />
            </div>

            <div>
              <label htmlFor="costScope" className="label">
                Cost scope
              </label>
              <select {...register("costScope")} id="costScope" className="input">
                {COST_SCOPES.map((cs) => (
                  <option key={cs} value={cs}>
                    {COST_SCOPE_LABELS[cs]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Fixed per departure (a chartered bus) or scales per person (insurance).
              </p>
            </div>

            <div>
              <label htmlFor="bookingMode" className="label">
                Booking mode
              </label>
              <select {...register("bookingMode")} id="bookingMode" className="input">
                {BOOKING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {BOOKING_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                “On demand” = our own / always-available (our boat): no supplier request, no confirm
                deadline, ready by default. Prefilled from the supplier; override here if needed.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Active
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
